import React, { useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Progress } from './ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Upload, Download, FileText, AlertCircle, CheckCircle, Clock, X } from 'lucide-react';
import { api } from '../api/client';

const BulkProductUpload = ({ onUploadComplete, onClose }) => {
  console.log('🔄 BulkProductUpload component rendered/re-rendered');
  
  const [currentStep, setCurrentStep] = useState(1); // 1: Upload, 2: Preview, 3: Processing
  const [uploadData, setUploadData] = useState(null);
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedTab, setSelectedTab] = useState('valid');
  
  // CRITICAL FIX: Validation dialog for new items
  const [validationDialog, setValidationDialog] = useState({
    open: false,
    newItems: { categories: [], brands: [], warehouses: [] },
    onConfirm: null,
    onCancel: null
  });
  
  // CRITICAL FIX: Warning details dialog
  const [warningDialog, setWarningDialog] = useState({
    open: false,
    warnings: [],
    rowData: null
  });
  
  console.log('📊 Current component state:', {
    currentStep,
    hasFile: !!file,
    fileName: file?.name,
    fileSize: file?.size,
    isUploading,
    isProcessing,
    hasUploadData: !!uploadData
  });

  // Download template with enhanced options
  const handleDownloadTemplate = async (format = 'csv', withSamples = false) => {
    try {
      const templateType = withSamples ? 'with-samples' : 'blank';
      
      // Use window.open for direct file download to avoid axios blob handling issues
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const url = `${backendUrl}/api/v1/products/bulk/template?format=${format}&type=${templateType}`;
      window.open(url, '_blank');
      
      if (window.toast) {
        const filename = withSamples 
          ? `product_template_with_samples.${format}`
          : `product_template_blank.${format}`;
        window.toast.success(`Downloading: ${filename}`);
      }
    } catch (error) {
      console.error('Template download error:', error);
      if (window.toast) {
        window.toast.error('Failed to download template. Please try again.');
      }
    }
  };

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      validateAndSetFile(droppedFile);
    }
  }, []);

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const validateAndSetFile = (selectedFile) => {
    console.log('📁 validateAndSetFile called with:', selectedFile);
    
    const allowedTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    const allowedExtensions = ['.csv', '.xlsx', '.xls'];
    const fileExtension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
    
    console.log('🔍 File validation - Type check:', {
      fileType: selectedFile.type,
      fileExtension,
      allowedTypes,
      allowedExtensions,
      typeMatch: allowedTypes.includes(selectedFile.type),
      extMatch: allowedExtensions.includes(fileExtension)
    });
    
    if (!allowedTypes.includes(selectedFile.type) && !allowedExtensions.includes(fileExtension)) {
      console.log('❌ File type validation failed');
      if (window.toast) {
        window.toast.error('Please select a CSV or Excel file (.csv, .xlsx, .xls)');
      }
      return;
    }

    // Enhanced file size validation with better messaging
    const maxSize = 10 * 1024 * 1024; // 10MB limit
    const minSize = 1; // Minimum 1 byte
    
    console.log('📏 File size validation:', {
      fileSize: selectedFile.size,
      maxSize,
      minSize,
      isZero: selectedFile.size === 0,
      exceedsMax: selectedFile.size > maxSize
    });
    
    if (selectedFile.size === 0) {
      console.log('❌ File size is 0 bytes');
      if (window.toast) {
        window.toast.error('Selected file is empty (0 bytes). Please choose a valid CSV or Excel file with data.');
      }
      return;
    }
    
    if (selectedFile.size > maxSize) {
      const sizeMB = (selectedFile.size / 1024 / 1024).toFixed(2);
      console.log('❌ File size exceeds limit:', sizeMB, 'MB');
      if (window.toast) {
        window.toast.error(`File size (${sizeMB}MB) exceeds 10MB limit. Please split large files into batches of 1,000 products or less.`);
      }
      return;
    }

    console.log('✅ File validation passed. File details:', {
      name: selectedFile.name,
      size: selectedFile.size,
      sizeInMB: (selectedFile.size / 1024 / 1024).toFixed(2),
      sizeInKB: (selectedFile.size / 1024).toFixed(2),
      type: selectedFile.type,
      lastModified: selectedFile.lastModified
    });
    
    // Warn if file size is 0
    if (selectedFile.size === 0) {
      console.warn('Warning: Selected file has 0 bytes. This may indicate a file reading issue.');
      if (window.toast) {
        window.toast.warning('File appears to be empty (0 bytes). Please check the file and try again.');
      }
    }
    
    console.log('📝 About to call setFile with:', selectedFile);
    setFile(selectedFile);
    console.log('✅ setFile called - file state should now be updated');
  };

  // Upload and parse file
  const handleUploadFile = async () => {
    console.log('🔵 PARSE BUTTON CLICKED! Function handleUploadFile called');
    console.log('📁 Current file state:', file);
    console.log('📊 Current isUploading state:', isUploading);
    console.log('📋 Current step:', currentStep);
    
    if (!file) {
      console.log('❌ NO FILE - Early return');
      console.log('File state is:', file);
      return;
    }

    console.log('✅ File exists, proceeding with validation...');
    console.log('📁 File details:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    });

    setIsUploading(true);
    console.log('⏳ Set isUploading to true');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      console.log('📤 Created FormData with file');

      console.log('🌐 Making API call to: /api/v1/products/bulk/parse');

      // API client returns response.data directly, not the full response
      const data = await api.post('/api/v1/products/bulk/parse', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('📥 Response received successfully');
      console.log('📊 Parsed response data:', data);
      
      // Check if we got valid data with the expected format
      if (data && data.upload_id && typeof data.total_rows !== 'undefined') {
        console.log('✅ Valid response data received - processing...');
        
        setUploadData(data);
        setCurrentStep(2);
        console.log('🔄 Set uploadData and currentStep to 2');
        
        if (window.toast) {
          // NEW RESPONSE FORMAT: { upload_id, status, total_rows, valid_rows, errors, warnings }
          const totalRows = data.total_rows || 0;
          const errorCount = data.errors?.length || 0;
          const warningCount = data.warnings?.length || 0;
          
          console.log('📈 Summary stats:', { totalRows, errorCount, warningCount });
          
          if (errorCount === 0) {
            window.toast.success(`✅ File validation complete! ${totalRows} products ready for upload`);
            console.log('🎉 Success toast shown');
          } else {
            window.toast.error(`❌ Validation found ${errorCount} error(s) in ${totalRows} products. Please review and fix errors.`);
            console.log('⚠️ Error toast shown');
          }
        } else {
          console.log('❌ window.toast not available');
        }
      } else {
        console.log('❌ Invalid response data format');
        console.log('📋 Response data:', data);
        throw new Error(data?.detail || data?.message || 'Failed to parse file - invalid response format');
      }
    } catch (error) {
      console.error('❌ UPLOAD ERROR:', error);
      console.error('❌ Error stack:', error.stack);
      if (window.toast) {
        window.toast.error(`Upload failed: ${error.message}`);
        console.log('🚨 Error toast shown');
      } else {
        console.log('❌ window.toast not available for error');
      }
    } finally {
      console.log('🏁 Finally block - setting isUploading to false');
      setIsUploading(false);
    }
  };

  // Execute upload
  const handleExecuteUpload = async () => {
    if (!uploadData?.upload_id) return;

    setIsProcessing(true);
    setCurrentStep(3);

    try {
      // API client returns data directly, not the full response
      const result = await api.post('/api/v1/products/bulk/execute', {
        upload_id: uploadData.upload_id,
        tenant_id: 'tenant-123'
      });

      // Check if we got valid result data
      if (result && result.results) {
        if (window.toast) {
          window.toast.success(`Upload completed! Created: ${result.results.created}, Updated: ${result.results.updated}, Deleted: ${result.results.deleted}`);
        }
        
        // Notify parent component
        if (onUploadComplete) {
          onUploadComplete(result);
        }
        
        // Auto close after success
        setTimeout(() => {
          onClose?.();
        }, 2000);
      } else {
        throw new Error(result?.detail || result?.message || 'Upload execution failed - invalid response');
      }
    } catch (error) {
      console.error('Execution error:', error);
      if (window.toast) {
        window.toast.error(`Upload failed: ${error.message}`);
      }
      setCurrentStep(2); // Go back to preview
    } finally {
      setIsProcessing(false);
    }
  };

  // Statistics Card Component
  const StatCard = ({ label, value, icon, color = 'blue' }) => (
    <Card className="dark:bg-gray-800">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{label}</p>
            <p className={`text-2xl font-bold text-${color}-600 dark:text-${color}-400`}>{value}</p>
          </div>
          <div className="text-2xl">{icon}</div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => onClose?.()}>
      <div 
        className="relative w-full max-w-6xl h-[90vh] mx-4 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={() => onClose?.()}
          className="absolute top-4 right-4 z-10 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Bulk Product Upload
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Upload multiple products via CSV or Excel file with validation and preview
          </p>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-6">
          <div className="flex items-center space-x-4">
            {[
              { step: 1, label: 'Upload', icon: Upload },
              { step: 2, label: 'Preview', icon: FileText },
              { step: 3, label: 'Process', icon: CheckCircle }
            ].map(({ step, label, icon: Icon }) => (
              <div key={step} className="flex items-center">
                <div className={`
                  flex items-center justify-center w-8 h-8 rounded-full border-2
                  ${currentStep >= step 
                    ? 'bg-blue-600 border-blue-600 text-white' 
                    : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                  }
                `}>
                  <Icon className="w-4 h-4" />
                </div>
                <span className={`
                  ml-2 text-sm font-medium
                  ${currentStep >= step ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}
                `}>
                  {label}
                </span>
                {step < 3 && <div className="w-8 h-0.5 bg-gray-300 dark:bg-gray-600 ml-4" />}
              </div>
            ))}
          </div>
        </div>

        {/* Step 1: File Upload */}
        {currentStep === 1 && (
          <div className="space-y-6">
            {/* Template Download Section */}
            <Card className="dark:bg-gray-800">
              <CardHeader>
                <CardTitle className="text-lg text-gray-900 dark:text-white">
                  Step 1: Download Template
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Choose from two template options: blank templates for your own data, or templates with sample products to guide your upload.
                </p>
                
                {/* Template Options Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <Card className="border border-gray-200 dark:border-gray-700">
                    <CardContent className="p-4">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">Blank Templates</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Headers only with validation rules</p>
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => handleDownloadTemplate('csv', false)} 
                          size="sm"
                          variant="outline"
                          className="dark:border-gray-600 dark:hover:bg-gray-800"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          CSV
                        </Button>
                        <Button 
                          onClick={() => handleDownloadTemplate('xlsx', false)} 
                          size="sm"
                          variant="outline"
                          className="dark:border-gray-600 dark:hover:bg-gray-800"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Excel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border border-green-200 dark:border-green-800 bg-green-50/30 dark:bg-green-900/10">
                    <CardContent className="p-4">
                      <h4 className="font-medium text-gray-900 dark:text-white mb-2">Sample Templates</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Headers + 3 sample products + instructions</p>
                      <div className="flex gap-2">
                        <Button 
                          onClick={() => handleDownloadTemplate('csv', true)} 
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          CSV
                        </Button>
                        <Button 
                          onClick={() => handleDownloadTemplate('xlsx', true)} 
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Excel
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Upload Limits Info */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <div className="text-blue-600 dark:text-blue-400 mt-0.5">ℹ️</div>
                    <div className="text-sm">
                      <p className="text-blue-800 dark:text-blue-200 font-medium mb-1">Upload Limits</p>
                      <ul className="text-blue-700 dark:text-blue-300 text-xs space-y-1">
                        <li>• Maximum file size: <strong>10MB</strong></li>
                        <li>• Maximum products per upload: <strong>1,000 items</strong></li>
                        <li>• Supported formats: <strong>CSV, Excel (.xlsx, .xls)</strong></li>
                        <li>• Operations supported: <strong>CREATE, UPDATE, DELETE</strong></li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Divider */}
            <div className="border-t border-gray-200 dark:border-gray-700 my-6"></div>

            {/* File Upload Section */}
            <Card className="dark:bg-gray-800">
              <CardHeader>
                <CardTitle className="text-lg text-gray-900 dark:text-white">
                  Step 2: Upload Your File
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Drag & Drop Area */}
                <div
                  className={`
                    border-2 border-dashed rounded-lg p-8 text-center transition-colors
                    ${dragActive 
                      ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' 
                      : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'
                    }
                  `}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <Upload className={`w-12 h-12 mx-auto mb-4 ${dragActive ? 'text-blue-500' : 'text-gray-400'}`} />
                  <p className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    {dragActive ? 'Drop your file here' : 'Drag and drop your product file'}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    Supports CSV and Excel files (.csv, .xlsx, .xls) up to 10MB
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mb-4">
                    or click the button below to browse files
                  </p>
                  
                  <input
                    type="file"
                    onChange={handleFileInput}
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload">
                    <Button variant="outline" className="dark:border-gray-600 dark:hover:bg-gray-700" asChild>
                      <span>Choose File</span>
                    </Button>
                  </label>
                  
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                    Supported formats: CSV, Excel (.csv, .xlsx, .xls) • Max size: 10MB
                  </p>
                </div>

                {/* Selected File Display */}
                {file && (
                  <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <FileText className="w-5 h-5 text-green-600 dark:text-green-400 mr-3" />
                        <div>
                          <p className="font-medium text-green-800 dark:text-green-200">{file.name}</p>
                          <p className="text-sm text-green-600 dark:text-green-400">
                            {file.size >= 1024 * 1024 
                              ? `${(file.size / 1024 / 1024).toFixed(2)} MB` 
                              : file.size >= 1024
                                ? `${(file.size / 1024).toFixed(2)} KB`
                                : `${file.size} bytes`
                            }
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setFile(null)}
                        className="text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={onClose} className="dark:border-gray-600 dark:hover:bg-gray-800">
                Cancel
              </Button>
              <Button 
                onClick={(e) => {
                  console.log('🖱️ BUTTON CLICKED - Event triggered');
                  console.log('📋 Button click event:', e);
                  console.log('📁 File at click time:', file);
                  console.log('⏳ isUploading at click time:', isUploading);
                  console.log('🚫 Button disabled?:', !file || isUploading);
                  handleUploadFile();
                }}
                disabled={!file || isUploading}
                className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
              >
                {(() => {
                  console.log('🔄 Button content rendering, isUploading:', isUploading);
                  return isUploading ? (
                    <>
                      <Clock className="w-4 h-4 mr-2 animate-spin" />
                      Parsing File...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Parse & Validate File
                    </>
                  );
                })()}
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Preview & Validation */}
        {currentStep === 2 && uploadData && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4">
              <StatCard
                label="Total Rows"
                value={uploadData.total_rows || 0}
                icon="📊"
              />
              <StatCard
                label="Valid"
                value={uploadData.valid_rows || 0}
                icon="✅"
                color="green"
              />
              <StatCard
                label="Errors"
                value={uploadData.errors?.length || 0}
                icon="❌"
                color="red"
              />
              <StatCard
                label="Warnings"
                value={uploadData.warnings?.length || 0}
                icon="⚠️"
                color="yellow"
              />
            </div>

            {/* Error Summary */}
            {uploadData.errors && uploadData.errors.length > 0 && (
              <Alert className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
                <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
                <AlertDescription className="text-red-800 dark:text-red-200">
                  <strong>{uploadData.errors.length} rows have errors</strong>
                  <p className="text-sm mt-1">Fix errors in your file before uploading</p>
                </AlertDescription>
              </Alert>
            )}

            {/* Data Preview Tabs */}
            <Tabs value={selectedTab} onValueChange={setSelectedTab}>
              <TabsList className="dark:bg-gray-800">
                <TabsTrigger value="valid" className="dark:data-[state=active]:bg-gray-700">
                  Valid ({uploadData.valid_rows || 0})
                </TabsTrigger>
                <TabsTrigger value="invalid" className="dark:data-[state=active]:bg-gray-700">
                  Errors ({uploadData.errors?.length || 0})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="valid" className="mt-4">
                <div className="max-h-96 overflow-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Row</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Operation</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">SKU</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Product Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Category</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Status</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Warnings</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {uploadData.valid_rows_data?.map((row) => (
                        <tr key={row.row_number} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{row.row_number}</td>
                          <td className="px-3 py-2">
                            <Badge className={
                              row.operation === 'CREATE' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                              row.operation === 'UPDATE' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                              'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                            }>
                              {row.operation}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100">{row.sku}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{row.product_name}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{row.category}</td>
                          <td className="px-3 py-2">
                            <Badge className={
                              row.status === 'active' 
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' 
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                            }>
                              {row.status}
                            </Badge>
                          </td>
                          <td className="px-3 py-2 text-sm">
                            {row.warnings?.length > 0 ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50"
                                onClick={() => setWarningDialog({
                                  open: true,
                                  warnings: row.warnings,
                                  rowData: row
                                })}
                              >
                                ⚠️ {row.warnings.length}
                              </Button>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              <TabsContent value="invalid" className="mt-4">
                <div className="max-h-96 overflow-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-red-50 dark:bg-red-900/20 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-red-700 dark:text-red-300">Row</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-red-700 dark:text-red-300">SKU</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-red-700 dark:text-red-300">Product Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-red-700 dark:text-red-300">Errors</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {uploadData.invalid_rows_data?.map((row) => (
                        <tr key={row.row_number} className="bg-red-50/30 dark:bg-red-900/10">
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{row.row_number}</td>
                          <td className="px-3 py-2 text-sm font-mono text-gray-900 dark:text-gray-100">{row.sku}</td>
                          <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{row.product_name}</td>
                          <td className="px-3 py-2">
                            <ul className="list-disc list-inside text-sm text-red-600 dark:text-red-400">
                              {row.errors?.map((error, i) => (
                                <li key={i}>{error}</li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>
            </Tabs>

            {/* Action Buttons */}
            <div className="flex justify-between items-center">
              <div className="text-sm">
                {uploadData.errors && uploadData.errors.length > 0 ? (
                  <span className="text-red-600 dark:text-red-400 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    Cannot upload - please fix {uploadData.errors.length} error(s)
                  </span>
                ) : (
                  <span className="text-green-600 dark:text-green-400 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Ready to upload {uploadData.valid_rows || 0} products
                  </span>
                )}
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setCurrentStep(1)} className="dark:border-gray-600 dark:hover:bg-gray-800">
                  Back to Upload
                </Button>
                <Button
                  onClick={handleExecuteUpload}
                  disabled={(uploadData.errors && uploadData.errors.length > 0) || isProcessing}
                  className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                >
                  {isProcessing ? (
                    <>
                      <Clock className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Upload Now ({uploadData.valid_rows || 0} products)
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Processing */}
        {currentStep === 3 && (
          <div className="text-center py-12">
            <div className="flex items-center justify-center mb-6">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-blue-200 dark:border-blue-800 rounded-full"></div>
                <div className="w-16 h-16 border-4 border-blue-600 dark:border-blue-400 rounded-full animate-spin border-t-transparent absolute top-0"></div>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Processing Upload...
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Creating, updating, and deleting products. This may take a moment.
            </p>
            <Progress value={85} className="w-64 mx-auto" />
          </div>
        )}
        </div>
      </div>
      
      {/* CRITICAL FIX: Validation Dialog for New Items */}
      <Dialog open={validationDialog.open} onOpenChange={(open) => {
        if (!open && validationDialog.onCancel) {
          validationDialog.onCancel();
        }
      }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>New Items Found in Upload</DialogTitle>
            <DialogDescription>
              Your upload contains new categories, brands, or warehouses that don't exist in the system.
              Would you like to create these items?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                These items will be created automatically if you proceed with the upload.
              </AlertDescription>
            </Alert>
            
            {validationDialog.newItems.categories.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2">
                  New Categories ({validationDialog.newItems.categories.length}):
                </h4>
                <div className="flex flex-wrap gap-2">
                  {validationDialog.newItems.categories.map(category => (
                    <Badge key={category} variant="outline" className="bg-blue-50">
                      {category}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {validationDialog.newItems.brands.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2">
                  New Brands ({validationDialog.newItems.brands.length}):
                </h4>
                <div className="flex flex-wrap gap-2">
                  {validationDialog.newItems.brands.map(brand => (
                    <Badge key={brand} variant="outline" className="bg-purple-50">
                      {brand}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {validationDialog.newItems.warehouses.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold mb-2">
                  New Warehouses ({validationDialog.newItems.warehouses.length}):
                </h4>
                <div className="flex flex-wrap gap-2">
                  {validationDialog.newItems.warehouses.map(warehouse => (
                    <Badge key={warehouse} variant="outline" className="bg-green-50">
                      {warehouse}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setValidationDialog({ ...validationDialog, open: false });
                if (validationDialog.onCancel) validationDialog.onCancel();
              }}
            >
              Cancel Upload
            </Button>
            <Button
              onClick={() => {
                setValidationDialog({ ...validationDialog, open: false });
                if (validationDialog.onConfirm) validationDialog.onConfirm();
              }}
            >
              Create Items & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* CRITICAL FIX: Warning Details Dialog */}
      <Dialog open={warningDialog.open} onOpenChange={(open) => {
        if (!open) setWarningDialog({ open: false, warnings: [], rowData: null });
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>⚠️ Warnings for Row</DialogTitle>
            <DialogDescription>
              {warningDialog.rowData && `SKU: ${warningDialog.rowData.sku || 'N/A'} - ${warningDialog.rowData.name || 'N/A'}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {warningDialog.warnings.map((warning, index) => (
              <Alert key={index} className="bg-yellow-50 border-yellow-200">
                <AlertCircle className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-sm text-yellow-800">
                  {typeof warning === 'string' ? warning : warning.message || JSON.stringify(warning)}
                </AlertDescription>
              </Alert>
            ))}
          </div>

          <DialogFooter>
            <Button onClick={() => setWarningDialog({ open: false, warnings: [], rowData: null })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BulkProductUpload;